import { useState, useEffect } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { 
  Plus, Edit2, Trash2, Loader2, Save, X, Image as ImageIcon, 
  Check, ChevronDown, ChevronRight, Upload, RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ImageUpload from "@/components/ImageUpload";

const API = `${getApiUrl()}/api`;

export default function SystemBarsConfig() {
  const [systemBars, setSystemBars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBars, setExpandedBars] = useState({});
  
  // Dialog states
  const [systemBarDialogOpen, setSystemBarDialogOpen] = useState(false);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  
  // Form states
  const [editingBar, setEditingBar] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null);
  const [selectedBarId, setSelectedBarId] = useState(null);
  
  const [newBar, setNewBar] = useState({
    systemName: "",
    displayLabel: "",
    color: "#333333",
    textColor: "#FFFFFF",
    sortOrder: 0
  });
  
  const [newVersion, setNewVersion] = useState({
    name: "",
    imageUrl: "",
    isActive: false
  });

  useEffect(() => {
    fetchSystemBars();
  }, []);

  const fetchSystemBars = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/config/system-bars`);
      setSystemBars(response.data.configs || []);
      // Expand all by default
      const expanded = {};
      response.data.configs?.forEach(bar => {
        expanded[bar.id] = true;
      });
      setExpandedBars(expanded);
    } catch (error) {
      console.error("Failed to fetch system bars:", error);
      toast.error("Failed to load system bar configurations");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBar = async () => {
    if (!newBar.systemName.trim()) {
      toast.error("System name is required");
      return;
    }
    try {
      await axios.post(`${API}/config/system-bars`, newBar);
      toast.success("System bar created");
      setSystemBarDialogOpen(false);
      setNewBar({ systemName: "", displayLabel: "", color: "#333333", textColor: "#FFFFFF", sortOrder: 0 });
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create system bar");
    }
  };

  const handleUpdateBar = async () => {
    if (!editingBar) return;
    try {
      await axios.put(`${API}/config/system-bars/${editingBar.id}`, {
        systemName: editingBar.systemName,
        displayLabel: editingBar.displayLabel,
        color: editingBar.color,
        textColor: editingBar.textColor,
        sortOrder: editingBar.sortOrder
      });
      toast.success("System bar updated");
      setSystemBarDialogOpen(false);
      setEditingBar(null);
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update system bar");
    }
  };

  const handleDeleteBar = async (barId) => {
    if (!window.confirm("Delete this system bar configuration?")) return;
    try {
      await axios.delete(`${API}/config/system-bars/${barId}`);
      toast.success("System bar deleted");
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete system bar");
    }
  };

  const handleAddVersion = async () => {
    if (!newVersion.name.trim() || !newVersion.imageUrl.trim()) {
      toast.error("Name and image URL are required");
      return;
    }
    try {
      await axios.post(`${API}/config/system-bars/${selectedBarId}/versions`, newVersion);
      toast.success("Version added");
      setVersionDialogOpen(false);
      setNewVersion({ name: "", imageUrl: "", isActive: false });
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add version");
    }
  };

  const handleUpdateVersion = async () => {
    if (!editingVersion) return;
    try {
      await axios.put(
        `${API}/config/system-bars/${selectedBarId}/versions/${editingVersion.id}`,
        {
          name: editingVersion.name,
          imageUrl: editingVersion.imageUrl,
          isActive: editingVersion.isActive
        }
      );
      toast.success("Version updated");
      setVersionDialogOpen(false);
      setEditingVersion(null);
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update version");
    }
  };

  const handleDeleteVersion = async (barId, versionId) => {
    if (!window.confirm("Delete this version?")) return;
    try {
      await axios.delete(`${API}/config/system-bars/${barId}/versions/${versionId}`);
      toast.success("Version deleted");
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete version");
    }
  };

  const handleActivateVersion = async (barId, versionId) => {
    try {
      await axios.post(`${API}/config/system-bars/${barId}/versions/${versionId}/activate`);
      toast.success("Version activated");
      fetchSystemBars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate version");
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm("Reset all system bars to defaults? This will remove any custom configurations.")) return;
    try {
      await axios.post(`${API}/config/system-bars/reset-defaults`);
      toast.success("Reset to defaults");
      fetchSystemBars();
    } catch (error) {
      toast.error("Failed to reset defaults");
    }
  };

  const openEditBarDialog = (bar) => {
    setEditingBar({ ...bar });
    setSystemBarDialogOpen(true);
  };

  const openAddVersionDialog = (barId) => {
    setSelectedBarId(barId);
    setEditingVersion(null);
    setNewVersion({ name: "", imageUrl: "", isActive: false });
    setVersionDialogOpen(true);
  };

  const openEditVersionDialog = (barId, version) => {
    setSelectedBarId(barId);
    setEditingVersion({ ...version });
    setVersionDialogOpen(true);
  };

  const toggleExpanded = (barId) => {
    setExpandedBars(prev => ({ ...prev, [barId]: !prev[barId] }));
  };

  if (loading) {
    return (
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white/50" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardHeader className="border-b border-white/10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              System Bars
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetDefaults}
                className="border-white/20 text-white/60 hover:text-white"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Reset Defaults
              </Button>
              <Button
                onClick={() => {
                  setEditingBar(null);
                  setNewBar({ systemName: "", displayLabel: "", color: "#333333", textColor: "#FFFFFF", sortOrder: 0 });
                  setSystemBarDialogOpen(true);
                }}
                className="bg-purple-600 hover:bg-purple-500"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add System
              </Button>
            </div>
          </div>
          <p className="text-sm text-white/50 mt-1">
            Configure banner images for each gaming system. Add multiple versions and set which one is active.
          </p>
        </CardHeader>
        <CardContent className="p-4">
          <div className="space-y-2">
            {systemBars.map((bar) => (
              <Collapsible
                key={bar.id}
                open={expandedBars[bar.id]}
                onOpenChange={() => toggleExpanded(bar.id)}
              >
                <div className="border border-white/10 rounded-lg overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 cursor-pointer transition-colors">
                      <div className="flex items-center gap-3">
                        {expandedBars[bar.id] ? (
                          <ChevronDown className="w-4 h-4 text-white/50" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/50" />
                        )}
                        <div 
                          className="w-6 h-6 rounded" 
                          style={{ backgroundColor: bar.color }}
                        />
                        <div>
                          <p className="font-medium text-white">{bar.systemName}</p>
                          <p className="text-xs text-white/50">
                            {bar.versions?.length || 0} version(s) • Label: {bar.displayLabel}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditBarDialog(bar)}
                          className="h-8 w-8 p-0 text-white/50 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBar(bar.id)}
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <div className="p-3 border-t border-white/10 bg-black/30">
                      {/* Versions List */}
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm text-white/60">Banner Versions</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAddVersionDialog(bar.id)}
                          className="h-7 text-xs border-white/20"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Version
                        </Button>
                      </div>
                      
                      {bar.versions?.length > 0 ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {bar.versions.map((version) => (
                            <div 
                              key={version.id}
                              className={`p-3 rounded-lg border transition-colors ${
                                version.isActive 
                                  ? "border-green-500/50 bg-green-500/10" 
                                  : "border-white/10 bg-white/5"
                              }`}
                            >
                              {/* Banner Preview */}
                              <div className="w-full h-12 bg-black/50 rounded overflow-hidden mb-2">
                                <img 
                                  src={version.imageUrl} 
                                  alt={version.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-medium text-white">{version.name}</p>
                                  {version.isActive && (
                                    <span className="text-[10px] text-green-400 flex items-center gap-1">
                                      <Check className="w-3 h-3" /> Active
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  {!version.isActive && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleActivateVersion(bar.id, version.id)}
                                      className="h-7 px-2 text-xs text-green-400 hover:text-green-300"
                                      title="Set as active"
                                    >
                                      <Check className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditVersionDialog(bar.id, version)}
                                    className="h-7 w-7 p-0 text-white/50 hover:text-white"
                                  >
                                    <Edit2 className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteVersion(bar.id, version.id)}
                                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-white/40">
                          <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No banner versions yet</p>
                          <p className="text-xs">Add a version to display a banner image</p>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
            
            {systemBars.length === 0 && (
              <div className="text-center py-8 text-white/40">
                <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No system bars configured</p>
                <p className="text-sm mt-1">Add a system to get started</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System Bar Dialog */}
      <Dialog open={systemBarDialogOpen} onOpenChange={setSystemBarDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBar ? "Edit System Bar" : "Add System Bar"}</DialogTitle>
            <DialogDescription className="text-white/50">
              Configure the system bar appearance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>System Name *</Label>
              <Input
                value={editingBar ? editingBar.systemName : newBar.systemName}
                onChange={(e) => editingBar 
                  ? setEditingBar({...editingBar, systemName: e.target.value})
                  : setNewBar({...newBar, systemName: e.target.value})
                }
                placeholder="e.g., Xbox 360"
                className="bg-black/50 border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Display Label</Label>
              <Input
                value={editingBar ? editingBar.displayLabel : newBar.displayLabel}
                onChange={(e) => editingBar 
                  ? setEditingBar({...editingBar, displayLabel: e.target.value})
                  : setNewBar({...newBar, displayLabel: e.target.value})
                }
                placeholder="e.g., XBOX 360"
                className="bg-black/50 border-white/10"
              />
              <p className="text-xs text-white/40">Shown as text fallback if no image</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Background Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={editingBar ? editingBar.color : newBar.color}
                    onChange={(e) => editingBar 
                      ? setEditingBar({...editingBar, color: e.target.value})
                      : setNewBar({...newBar, color: e.target.value})
                    }
                    className="w-12 h-10 p-1 bg-black/50 border-white/10"
                  />
                  <Input
                    value={editingBar ? editingBar.color : newBar.color}
                    onChange={(e) => editingBar 
                      ? setEditingBar({...editingBar, color: e.target.value})
                      : setNewBar({...newBar, color: e.target.value})
                    }
                    className="flex-1 bg-black/50 border-white/10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Text Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={editingBar ? editingBar.textColor : newBar.textColor}
                    onChange={(e) => editingBar 
                      ? setEditingBar({...editingBar, textColor: e.target.value})
                      : setNewBar({...newBar, textColor: e.target.value})
                    }
                    className="w-12 h-10 p-1 bg-black/50 border-white/10"
                  />
                  <Input
                    value={editingBar ? editingBar.textColor : newBar.textColor}
                    onChange={(e) => editingBar 
                      ? setEditingBar({...editingBar, textColor: e.target.value})
                      : setNewBar({...newBar, textColor: e.target.value})
                    }
                    className="flex-1 bg-black/50 border-white/10"
                  />
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="space-y-2">
              <Label>Preview (fallback)</Label>
              <div 
                className="h-10 rounded flex items-center justify-center font-bold text-sm tracking-wider"
                style={{ 
                  backgroundColor: editingBar ? editingBar.color : newBar.color,
                  color: editingBar ? editingBar.textColor : newBar.textColor
                }}
              >
                {(editingBar ? editingBar.displayLabel : newBar.displayLabel) || "SYSTEM NAME"}
              </div>
            </div>
            
            <Button 
              onClick={editingBar ? handleUpdateBar : handleCreateBar}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              {editingBar ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingBar ? "Save Changes" : "Create System Bar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Version Dialog */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingVersion ? "Edit Version" : "Add Version"}</DialogTitle>
            <DialogDescription className="text-white/50">
              Add a banner image for this system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Version Name *</Label>
              <Input
                value={editingVersion ? editingVersion.name : newVersion.name}
                onChange={(e) => editingVersion 
                  ? setEditingVersion({...editingVersion, name: e.target.value})
                  : setNewVersion({...newVersion, name: e.target.value})
                }
                placeholder="e.g., Default, Classic, Alternate"
                className="bg-black/50 border-white/10"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Banner Image</Label>
              <div className="flex gap-3">
                <ImageUpload
                  folder="gamepop/system-bars"
                  currentImageUrl={editingVersion ? editingVersion.imageUrl : newVersion.imageUrl}
                  onImageUploaded={(url) => editingVersion 
                    ? setEditingVersion({...editingVersion, imageUrl: url})
                    : setNewVersion({...newVersion, imageUrl: url})
                  }
                  size="lg"
                  aspectRatio="banner"
                />
                <div className="flex-1 space-y-2">
                  <Input
                    value={editingVersion ? editingVersion.imageUrl : newVersion.imageUrl}
                    onChange={(e) => editingVersion 
                      ? setEditingVersion({...editingVersion, imageUrl: e.target.value})
                      : setNewVersion({...newVersion, imageUrl: e.target.value})
                    }
                    placeholder="Or paste image URL..."
                    className="bg-black/50 border-white/10"
                  />
                  <p className="text-xs text-white/40">Upload an image or paste a URL</p>
                </div>
              </div>
            </div>
            
            {/* Preview */}
            {(editingVersion?.imageUrl || newVersion.imageUrl) && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="h-16 bg-black/50 rounded overflow-hidden">
                  <img 
                    src={editingVersion ? editingVersion.imageUrl : newVersion.imageUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={editingVersion ? editingVersion.isActive : newVersion.isActive}
                onChange={(e) => editingVersion 
                  ? setEditingVersion({...editingVersion, isActive: e.target.checked})
                  : setNewVersion({...newVersion, isActive: e.target.checked})
                }
                className="rounded border-white/20"
              />
              <Label htmlFor="isActive" className="cursor-pointer">Set as active version</Label>
            </div>
            
            <Button 
              onClick={editingVersion ? handleUpdateVersion : handleAddVersion}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              {editingVersion ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {editingVersion ? "Save Changes" : "Add Version"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
